/**
 * Profile photo editor: pick or take a photo, crop it to a square by hand (drag to pan,
 * slider / pinch / wheel to zoom), export 512 + 128 px JPEGs, upload and commit.
 * AvatarEditor (own photo) is used by first-run onboarding and the Settings screen;
 * MemberAvatarEditor (another member's photo, editors/admins) by the member profile and the admin
 * member sheet. Uses no router hooks.
 */
import { Camera, Check, ImagePlus, Trash, ZoomIn, ZoomOut } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
  type KeyboardEvent,
  type PointerEvent,
  type SetStateAction,
} from 'react';
import type { Section } from '../../shared/audience';
import type { MeDTO, UserStatus } from '../../shared/dto/auth';
import { removeAvatar, uploadAvatar } from '../api/auth';
import { useMemberAvatar } from '../api/members';
import { useT } from '../i18n';
import {
  MAX_ZOOM,
  cropToSquareJpeg,
  initialCrop,
  loadImage,
  makePreview,
  panCrop,
  zoomCropAt,
  zoomOf,
  type LoadedImage,
  type SquareCrop,
} from '../lib/image';
import { apiErrorText } from '../screens/auth/errors';
import { Avatar } from './people';
import { useToast } from './toast';
import { Button, ConfirmSheet, IconButton, Sheet, cx } from './ui';

/** Phone photos are a few MB; refuse absurd inputs before decoding. */
const MAX_INPUT_BYTES = 40 * 1024 * 1024;
const KEY_PAN_PX = 24;
const KEY_ZOOM = 1.15;

interface Editing {
  img: LoadedImage;
  preview: { url: string; width: number; height: number };
}

type Pt = { x: number; y: number };
const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/* ─── Square crop viewport ──────────────────────────────────────────────── */

function CropViewport({ editing, crop, setCrop }: { editing: Editing; crop: SquareCrop; setCrop: Dispatch<SetStateAction<SquareCrop | null>> }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(0);
  const pointers = useRef(new Map<number, Pt>());
  const { img, preview } = editing;
  const W = img.width;
  const H = img.height;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setView(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const update = (fn: (c: SquareCrop) => SquareCrop) => setCrop((c) => (c ? fn(c) : c));

  // Wheel / trackpad zoom around the cursor (needs a non-passive listener to stop page scroll).
  useEffect(() => {
    const el = ref.current;
    if (!el || view <= 0) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.002));
      setCrop((c) => (c ? zoomCropAt(c, zoomOf(c, W, H) * factor, e.clientX - r.left, e.clientY - r.top, view, W, H) : c));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [view, W, H, setCrop]);

  const local = (e: PointerEvent<HTMLDivElement>): Pt => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.focus({ preventScroll: true });
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
    pointers.current.set(e.pointerId, local(e));
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const map = pointers.current;
    const prev = map.get(e.pointerId);
    if (!prev || view <= 0) return;
    const cur = local(e);
    if (map.size === 1) {
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      update((c) => panCrop(c, dx, dy, view, W, H));
    } else {
      let other: Pt | undefined;
      for (const [id, p] of map) if (id !== e.pointerId) other = p;
      if (other) {
        const o = other;
        const d0 = dist(prev, o);
        const d1 = dist(cur, o);
        const m0 = mid(prev, o);
        const m1 = mid(cur, o);
        update((c) => {
          const z = zoomOf(c, W, H) * (d0 > 4 ? d1 / d0 : 1);
          return panCrop(zoomCropAt(c, z, m0.x, m0.y, view, W, H), m1.x - m0.x, m1.y - m0.y, view, W, H);
        });
      }
    }
    map.set(e.pointerId, cur);
  };

  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const c = view / 2;
    let handled = true;
    switch (e.key) {
      case 'ArrowLeft':
        update((cr) => panCrop(cr, -KEY_PAN_PX, 0, view, W, H));
        break;
      case 'ArrowRight':
        update((cr) => panCrop(cr, KEY_PAN_PX, 0, view, W, H));
        break;
      case 'ArrowUp':
        update((cr) => panCrop(cr, 0, -KEY_PAN_PX, view, W, H));
        break;
      case 'ArrowDown':
        update((cr) => panCrop(cr, 0, KEY_PAN_PX, view, W, H));
        break;
      case '+':
      case '=':
        update((cr) => zoomCropAt(cr, zoomOf(cr, W, H) * KEY_ZOOM, c, c, view, W, H));
        break;
      case '-':
      case '_':
        update((cr) => zoomCropAt(cr, zoomOf(cr, W, H) / KEY_ZOOM, c, c, view, W, H));
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  };

  const s = view > 0 ? view / crop.size : 0; // screen px per source px
  const r = W / preview.width; // source px per preview px

  return (
    <div
      ref={ref}
      role="application"
      aria-label={t('auth.avatar.viewport')}
      tabIndex={0}
      className="no-select relative mx-auto aspect-square cursor-grab overflow-hidden rounded-2xl bg-black active:cursor-grabbing"
      style={{ width: 'min(100%, 420px, 52dvh)', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onLostPointerCapture={onPointerEnd}
      onKeyDown={onKeyDown}
    >
      {view > 0 ? (
        <img
          src={preview.url}
          alt=""
          draggable={false}
          className="pointer-events-none absolute top-0 left-0 select-none"
          style={{
            width: preview.width,
            height: preview.height,
            maxWidth: 'none',
            transformOrigin: '0 0',
            transform: `translate3d(${-crop.x * s}px, ${-crop.y * s}px, 0) scale(${s * r})`,
            willChange: 'transform',
          }}
        />
      ) : null}
      {/* Round guide: avatars are shown as circles. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/80"
        style={{ boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.45)' }}
      />
    </div>
  );
}

function ZoomControls({ editing, crop, setCrop }: { editing: Editing; crop: SquareCrop; setCrop: Dispatch<SetStateAction<SquareCrop | null>> }) {
  const t = useT();
  const W = editing.img.width;
  const H = editing.img.height;
  const zoom = zoomOf(crop, W, H);
  // Zoom around the centre; the viewport size cancels out when the focus is its middle.
  const setZoom = (z: number) => setCrop((c) => (c ? zoomCropAt(c, z, 0.5, 0.5, 1, W, H) : c));
  return (
    <div className="mx-auto mt-4 flex w-full max-w-[420px] items-center gap-2">
      <IconButton icon={ZoomOut} label={t('auth.avatar.zoom_out')} onClick={() => setZoom(zoom / KEY_ZOOM)} disabled={zoom <= 1.001} />
      <input
        type="range"
        min={1}
        max={MAX_ZOOM}
        step={0.01}
        value={zoom}
        onChange={(e) => setZoom(Number(e.target.value))}
        aria-label={t('auth.avatar.zoom')}
        className="h-11 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
      />
      <IconButton icon={ZoomIn} label={t('auth.avatar.zoom_in')} onClick={() => setZoom(zoom * KEY_ZOOM)} disabled={zoom >= MAX_ZOOM - 0.001} />
    </div>
  );
}

/* ─── Editor ────────────────────────────────────────────────────────────── */

/** Whose photo is being changed. */
interface AvatarSubject {
  id: string;
  fullName: string;
  avatarRev: number;
  section: Section | null;
  /** 'inactive': cannot sign in, so cannot change the photo themselves (noted in the crop sheet). */
  status?: UserStatus;
}

/**
 * 'own': Settings / onboarding — large preview, front camera, "your initials".
 * 'member': an editor or admin changing another member's photo — buttons only (the profile or sheet
 * around it shows the photo), rear camera, every label names the member.
 */
type EditorMode = 'own' | 'member';

function PhotoEditor({
  subject,
  mode,
  upload,
  remove: removePhoto,
  onSaved,
  className,
}: {
  subject: AvatarSubject;
  mode: EditorMode;
  upload: (big: Blob, small: Blob) => Promise<unknown>;
  remove: () => Promise<unknown>;
  onSaved?: () => void;
  className?: string;
}) {
  const t = useT();
  const toast = useToast();
  const pickRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const chooseRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [crop, setCrop] = useState<SquareCrop | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const coarse = useMemo(() => window.matchMedia?.('(pointer: coarse)').matches === true, []);
  const own = mode === 'own';
  const name = { name: subject.fullName };
  const hasPhoto = subject.avatarRev > 0;

  // After a removal the "Fjarlægja mynd" button (the confirm sheet's opener) unmounts, so focus would
  // fall to <body>: once the confirm sheet is closed and the button is gone (in whichever order those
  // renders land), put focus on the choose/add button.
  const refocusAfterRemove = useRef(false);
  useEffect(() => {
    if (!refocusAfterRemove.current || hasPhoto || confirmRemove || removing) return;
    refocusAfterRemove.current = false;
    const a = document.activeElement;
    if (!a || a === document.body) chooseRef.current?.focus({ preventScroll: true });
  }, [hasPhoto, confirmRemove, removing]);

  // Decoded pixels + preview URL of the current session, freed on close and on unmount.
  const editingRef = useRef<Editing | null>(null);
  const release = () => {
    const cur = editingRef.current;
    editingRef.current = null;
    if (cur) {
      cur.img.release();
      URL.revokeObjectURL(cur.preview.url);
    }
  };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      release();
    };
  }, []);

  const close = () => {
    release();
    setEditing(null);
    setCrop(null);
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again
    if (!file) return;
    if (file.size > MAX_INPUT_BYTES) {
      toast.show(t('auth.avatar.too_big'), { tone: 'error' });
      return;
    }
    if (file.type && !file.type.startsWith('image/')) {
      toast.show(t('auth.avatar.read_error'), { tone: 'error' });
      return;
    }
    setLoading(true);
    let img: LoadedImage | null = null;
    try {
      img = await loadImage(file);
      const preview = await makePreview(img);
      if (!mounted.current) {
        img.release();
        URL.revokeObjectURL(preview.url);
        return;
      }
      release();
      editingRef.current = { img, preview };
      setEditing({ img, preview });
      setCrop(initialCrop(img.width, img.height));
    } catch {
      img?.release();
      toast.show(t('auth.avatar.read_error'), { tone: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!editing || !crop || saving) return;
    setSaving(true);
    try {
      const big = await cropToSquareJpeg(editing.img, crop, 512);
      const small = await cropToSquareJpeg(editing.img, crop, 128);
      await upload(big, small);
      toast.show(own ? t('auth.avatar.saved') : t('auth.avatar.member_saved', name), { tone: 'ok' });
      close();
      onSaved?.();
    } catch (err) {
      toast.show(apiErrorText(t, err), { tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await removePhoto();
      refocusAfterRemove.current = true;
      setConfirmRemove(false);
      toast.show(own ? t('auth.avatar.removed') : t('auth.avatar.member_removed', name), { tone: 'ok' });
      onSaved?.();
    } catch (err) {
      toast.show(apiErrorText(t, err), { tone: 'error' });
    } finally {
      setRemoving(false);
    }
  };

  const busy = loading || saving || removing;
  const chooseLabel = loading
    ? t('auth.avatar.loading')
    : own
      ? t('auth.avatar.choose')
      : t(hasPhoto ? 'auth.avatar.member_change' : 'auth.avatar.member_add');

  const buttons = (
    <>
      <Button
        ref={chooseRef}
        size={own ? 'lg' : 'md'}
        variant={own ? 'primary' : 'secondary'}
        icon={ImagePlus}
        busy={loading}
        disabled={busy}
        aria-label={own || loading ? undefined : t(hasPhoto ? 'auth.avatar.member_change_label' : 'auth.avatar.member_add_label', name)}
        onClick={() => pickRef.current?.click()}
      >
        {chooseLabel}
      </Button>
      {coarse ? (
        <Button
          size={own ? 'lg' : 'md'}
          variant="secondary"
          icon={Camera}
          disabled={busy}
          aria-label={own ? undefined : t('auth.avatar.member_take_label', name)}
          onClick={() => camRef.current?.click()}
        >
          {t('auth.avatar.take')}
        </Button>
      ) : null}
      {hasPhoto ? (
        <Button
          variant="ghost"
          icon={Trash}
          disabled={busy}
          aria-label={own ? undefined : t('auth.avatar.member_remove_label', name)}
          onClick={() => setConfirmRemove(true)}
        >
          {t('auth.avatar.remove')}
        </Button>
      ) : null}
    </>
  );

  return (
    <div
      className={cx(
        own ? 'flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-7' : 'flex flex-wrap items-center gap-2',
        className,
      )}
    >
      {own ? (
        <>
          <Avatar
            userId={subject.id}
            name={subject.fullName}
            rev={subject.avatarRev}
            section={subject.section}
            size={144}
            className="shadow-[var(--shadow-card)] ring-4 ring-[var(--surface)]"
          />
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-64">{buttons}</div>
        </>
      ) : (
        buttons
      )}

      {/* Visually hidden (not display:none) so programmatic click() works in iOS Safari. */}
      <input ref={pickRef} type="file" accept="image/jpeg,image/png" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void onFile(e)} />
      <input
        ref={camRef}
        type="file"
        accept="image/jpeg,image/png"
        capture={own ? 'user' : 'environment'}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => void onFile(e)}
      />

      <Sheet
        open={editing !== null && crop !== null}
        onClose={() => {
          if (!saving) close();
        }}
        title={own ? t('auth.avatar.crop_title') : t('auth.avatar.member_crop_title', name)}
        footer={
          <>
            <Button variant="secondary" size="lg" block disabled={saving} onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button size="lg" block icon={Check} busy={saving} onClick={() => void save()}>
              {t('auth.avatar.save')}
            </Button>
          </>
        }
      >
        {editing && crop ? (
          <div className="pb-2">
            <p className="mb-4 text-center text-[16px] text-[var(--muted)]">{t('auth.avatar.crop_hint')}</p>
            <CropViewport editing={editing} crop={crop} setCrop={setCrop} />
            <ZoomControls editing={editing} crop={crop} setCrop={setCrop} />
            {own ? null : (
              <p className="mx-auto mt-4 max-w-[420px] text-center text-[15px] text-[var(--muted)]">
                {t('auth.avatar.member_consent')}
                {subject.status === 'inactive' ? <span className="mt-1 block">{t('auth.avatar.member_inactive_note')}</span> : null}
              </p>
            )}
          </div>
        ) : null}
      </Sheet>

      <ConfirmSheet
        open={confirmRemove}
        title={own ? t('auth.avatar.remove_confirm') : t('auth.avatar.member_remove_confirm', name)}
        body={own ? t('auth.avatar.remove_body') : t('auth.avatar.member_remove_body')}
        confirmLabel={t('auth.avatar.remove')}
        cancelLabel={t('common.cancel')}
        danger
        busy={removing}
        onConfirm={() => void remove()}
        onClose={() => setConfirmRemove(false)}
      />
    </div>
  );
}

/** The signed-in user's own photo (Settings, first-run onboarding). */
export function AvatarEditor({ me, onSaved }: { me: MeDTO; onSaved?: () => void }) {
  return <PhotoEditor subject={me} mode="own" upload={(big, small) => uploadAvatar(me, big, small)} remove={removeAvatar} onSaved={onSaved} />;
}

/**
 * Another member's photo, for editors (the conductor) and admins: "Bæta við mynd" / "Breyta mynd",
 * "Taka mynd" (iPad) and "Fjarlægja mynd". Show it only when canManageAvatarOf(me, member) — the
 * server refuses everyone else. Refreshes the directory, profile, admin lists, comment boards,
 * rosters and statistics on success.
 */
export function MemberAvatarEditor({ member, className }: { member: AvatarSubject; className?: string }) {
  const change = useMemberAvatar();
  return (
    <PhotoEditor
      subject={member}
      mode="member"
      upload={(big, small) => change.mutateAsync({ member, blobs: { big, small } })}
      remove={() => change.mutateAsync({ member, blobs: null })}
      className={className}
    />
  );
}

/** Default export too, so both `import { AvatarEditor }` and `import AvatarEditor` work. */
export default AvatarEditor;
